// Comprobante de una venta cerrada (Mis ventas y Ventas): las líneas congeladas con su forma de pago
// y, si la venta trae el reparto, lo financiado y lo de contado. Puro.
import type { SaleLine } from '@/api/services/retail-wizard/contract';
import { GIFT_LABEL, saleLineRows, type SaleLineRow } from './saleLines';
import { paymentMethodName } from './salePaymentLines';

export interface ReceiptSplit {
    financedCents: number;
    cashCents: number;
}

export interface SaleReceiptRow extends SaleLineRow {
    /** La línea congelada de la que sale la fila. */
    line: SaleLine;
    /** Precio de la línea menos su descuento. */
    netCents: number;
    /** `null` en las ventas sin reparto: solo se sabe la forma de pago de la línea. */
    split: ReceiptSplit | null;
}

export interface SaleReceipt {
    rows: SaleReceiptRow[];
    totalCents: number;
    /** Solo cuando todas las líneas traen el reparto. */
    totals: ReceiptSplit | null;
}

function splitLabel({ financedCents, cashCents }: ReceiptSplit): string {
    if (financedCents === 0) return 'Contado';
    if (cashCents === 0) return 'Crédito SureKeep';
    return 'Crédito SureKeep y contado';
}

function toReceiptRow(line: SaleLine, row: SaleLineRow): SaleReceiptRow {
    const netCents = line.basePriceCents * line.qty - (line.discountCents ?? 0);
    if (line.financedCents === undefined) return { ...row, line, netCents, split: null };
    const split = { financedCents: line.financedCents, cashCents: netCents - line.financedCents };
    const paymentLabel = line.role === 'GIFT' ? GIFT_LABEL : splitLabel(split);
    return { ...row, line, netCents, split, paymentLabel };
}

/** `null` en las ventas anteriores a las líneas: no hay nada congelado que listar. */
export function saleReceipt(lines: SaleLine[] | undefined): SaleReceipt | null {
    const rows = saleLineRows(lines);
    if (!lines || !rows) return null;
    const receiptRows = lines.map((line, i) => toReceiptRow(line, rows[i]));
    const splits = receiptRows.map((r) => r.split);
    const totals = splits.every((s): s is ReceiptSplit => s !== null)
        ? {
              financedCents: splits.reduce((sum, s) => sum + s.financedCents, 0),
              cashCents: splits.reduce((sum, s) => sum + s.cashCents, 0),
          }
        : null;
    return {
        rows: receiptRows,
        totalCents: receiptRows.reduce((sum, r) => sum + r.netCents, 0),
        totals,
    };
}

/** Importes de la venta por sus líneas: equipo (teléfono o kit), accesorios y total; los regalos no suman. */
export interface SaleAmounts {
    equipmentCents: number;
    accessoriesCents: number;
    totalCents: number;
}

const EQUIPMENT_CATEGORIES: ReadonlySet<string> = new Set(['PHONE', 'BUNDLE']);

/** Sin teléfono (`withEquipment` falso) todo lo cobrado cuenta como accesorios. */
export function saleAmounts(
    lines: SaleLine[] | undefined,
    withEquipment = true,
): SaleAmounts | null {
    const receipt = saleReceipt(lines);
    if (!receipt) return null;
    let equipmentCents = 0;
    let accessoriesCents = 0;
    for (const row of receipt.rows) {
        if (row.line.role === 'GIFT') continue;
        const category = row.line.product?.category;
        if (withEquipment && category !== undefined && EQUIPMENT_CATEGORIES.has(category))
            equipmentCents += row.netCents;
        else accessoriesCents += row.netCents;
    }
    return { equipmentCents, accessoriesCents, totalCents: equipmentCents + accessoriesCents };
}

/** Forma de pago y método de cobro en una frase: «Contado · Efectivo», «SureKeep · enganche en efectivo». */
export function receiptPaymentSummary({
    type,
    paymentMethod,
    financier,
}: {
    type: string;
    paymentMethod: string | null;
    /** Nombre de la financiera externa, si la hay. */
    financier?: string | null;
}): string | null {
    const method = paymentMethod ? paymentMethodName(paymentMethod) : null;
    const lower = method?.toLowerCase() ?? null;
    if (type === 'CREDIT' && !financier) {
        return lower ? `SureKeep · enganche en ${lower}` : 'SureKeep';
    }
    if (financier) return method ? `${financier} · ${method}` : financier;
    if (type === 'CASH') return method ? `Contado · ${method}` : 'Contado';
    return method;
}
